import { Schema } from "prosemirror-model";
import { RichTextEditorMarks, RichTextEditorNodes } from "../schema";
import { useMemo, useState } from "react";

type SupportedCallbackWithSchemaResult<
	TCallbackArguments extends readonly unknown[],
	TCallbackReturnType
> = {
	isSupportedBySchema: true;
} & ((...args: TCallbackArguments) => TCallbackReturnType);

export type CallbackWithSchemaResult<
	TCallbackArguments extends readonly unknown[],
	TCallbackReturnType
> = {
	isSupportedBySchema: false;
} | SupportedCallbackWithSchemaResult<TCallbackArguments, TCallbackReturnType>;

export function useExtendWithSchema<
	TCallbackArguments extends readonly unknown[],
	TCallbackReturnType,
	TNodes extends RichTextEditorNodes = never,
	TMarks extends RichTextEditorMarks = never
>(
	schema: Schema<never, never>,
	schemaCheck: (schema: Schema<never, never>) => schema is Schema<TNodes, TMarks>,
	callback: (schema: Schema<TNodes, TMarks>, ...args: TCallbackArguments) => TCallbackReturnType
): CallbackWithSchemaResult<TCallbackArguments, TCallbackReturnType> {
	const [stableSchemaCheck] = useState<typeof schemaCheck>((): typeof schemaCheck => schemaCheck);

	return useMemo(
		(): CallbackWithSchemaResult<TCallbackArguments, TCallbackReturnType> => {
			if (!stableSchemaCheck(schema)) {
				return {
					isSupportedBySchema: false
				};
			}

			const result = ((...args: TCallbackArguments): TCallbackReturnType => {
				return callback(schema, ...args);
			}) as SupportedCallbackWithSchemaResult<TCallbackArguments, TCallbackReturnType>;

			result.isSupportedBySchema = true;

			return result;
		},
		[
			callback,
			schema,
			stableSchemaCheck
		]
	);
}